// identity2 (Better Auth) authentication service. Replaces the Cognito OAuth/PKCE
// service. Redirect-based sign-in to the hosted auth UI; silent JWT refresh via
// the cross-subdomain session cookie.
import {
  auth2Config,
  validateAuth2Config,
  generateState,
  buildLoginUrl,
  buildLogoutUrl,
} from '@/config/auth2'
import type { AuthTokens, AuthUser } from '@/config/auth2'
import { captureSentryException, addSentryBreadcrumb } from '@/config/sentry'

class AuthService {
  private static instance: AuthService

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService()
    }
    return AuthService.instance
  }

  /** Redirect to the hosted auth UI to sign in. `registry` kept for call-site compatibility. */
  public redirectToLogin(registry: string = 'sor'): void {
    try {
      if (!validateAuth2Config()) {
        throw new Error('Auth configuration is incomplete')
      }

      const state = generateState()
      sessionStorage.setItem('auth_state', state)
      sessionStorage.setItem('auth_registry', registry)
      localStorage.setItem('auth_state_backup', state)
      localStorage.setItem('auth_state_timestamp', Date.now().toString())

      addSentryBreadcrumb(`Redirecting to auth UI for registry: ${registry}`, 'navigation')
      window.location.href = buildLoginUrl(state)
    } catch (error) {
      captureSentryException(error as Error, { service: 'AuthService', action: 'redirectToLogin', registry })
      throw error
    }
  }

  /**
   * Reads the JWT + state from the callback URL fragment, validates the state
   * against the value stored before redirect, and returns the token.
   */
  public handleCallback(): AuthTokens {
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const token = fragment.get('token') || ''
    const state = fragment.get('state') || ''

    if (!token) {
      throw new Error('Missing token in callback')
    }

    let storedState = sessionStorage.getItem('auth_state')
    if (!storedState) {
      const backup = localStorage.getItem('auth_state_backup')
      const timestamp = localStorage.getItem('auth_state_timestamp')
      if (backup && timestamp && Date.now() - parseInt(timestamp) < 10 * 60 * 1000) {
        storedState = backup
      }
    }

    if (!state || state !== storedState) {
      captureSentryException(new Error('State mismatch on auth callback'), {
        service: 'AuthService',
        action: 'handleCallback',
      })
      throw new Error('State parameter mismatch - possible CSRF attack')
    }

    sessionStorage.removeItem('auth_state')
    sessionStorage.removeItem('auth_registry')
    localStorage.removeItem('auth_state_backup')
    localStorage.removeItem('auth_state_timestamp')

    return { token }
  }

  /**
   * Silently mints a fresh JWT from identity2 using the cross-subdomain session
   * cookie. Replaces the Cognito refresh-token grant. Throws if there's no valid
   * session (caller should redirect to login).
   */
  public async refreshTokens(): Promise<AuthTokens> {
    try {
      const response = await fetch(`${auth2Config.baseUrl}/api/auth/token`, {
        method: 'GET',
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error(`Session expired (${response.status})`)
      }

      const data = await response.json() as { token?: string }
      if (!data.token) {
        throw new Error('No token returned from auth service')
      }

      addSentryBreadcrumb('Token refreshed', 'auth')
      return { token: data.token }
    } catch (error) {
      // Expected when the session cookie is gone — not an alertable error.
      addSentryBreadcrumb('Silent token refresh failed', 'auth', 'warning')
      throw error
    }
  }

  /** Redirect to the hosted auth UI to sign out (clears the session cookie). */
  public logout(): void {
    try {
      this.clearLocalAuth()
      window.location.href = buildLogoutUrl()
    } catch (error) {
      captureSentryException(error as Error, { service: 'AuthService', action: 'logout' })
      throw error
    }
  }

  public clearLocalAuth(): void {
    try {
      sessionStorage.removeItem('auth_state')
      sessionStorage.removeItem('auth_registry')
      localStorage.removeItem('auth_state_backup')
      localStorage.removeItem('auth_state_timestamp')
      localStorage.removeItem('auth_tokens')
      localStorage.removeItem('user_info')
    } catch (error) {
      captureSentryException(error as Error, { service: 'AuthService', action: 'clearLocalAuth' })
    }
  }

  public decodeJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1]
      if (!base64Url) {
        throw new Error('Invalid JWT token format')
      }
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(char => '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      throw new Error('Invalid JWT token')
    }
  }

  /** Decodes the user view (email / sub / name) straight from the JWT. */
  public userFromToken(token: string): AuthUser {
    const payload = this.decodeJWT(token)
    return {
      sub: payload.sub || '',
      email: payload.email || '',
      name: payload.name,
    }
  }

  public isTokenExpired(token: string): boolean {
    try {
      const decoded = this.decodeJWT(token)
      const currentTime = Math.floor(Date.now() / 1000)
      return decoded.exp < currentTime
    } catch (error) {
      return true
    }
  }

  /**
   * On app init: restore the stored token, refreshing silently if expired.
   */
  public async validateAuthState(): Promise<{ isAuthenticated: boolean; user?: AuthUser }> {
    try {
      const storedTokens = localStorage.getItem('auth_tokens')
      if (!storedTokens) {
        return { isAuthenticated: false }
      }

      let tokens = JSON.parse(storedTokens) as AuthTokens

      if (!tokens.token || this.isTokenExpired(tokens.token)) {
        try {
          tokens = await this.refreshTokens()
          localStorage.setItem('auth_tokens', JSON.stringify(tokens))
        } catch {
          this.clearLocalAuth()
          return { isAuthenticated: false }
        }
      }

      return { isAuthenticated: true, user: this.userFromToken(tokens.token) }
    } catch (error) {
      captureSentryException(error as Error, { service: 'AuthService', action: 'validateAuthState' })
      this.clearLocalAuth()
      return { isAuthenticated: false }
    }
  }
}

export const authService = AuthService.getInstance()
